// src/components/DocSign/DocSignHistory.jsx

import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    CardHeader,
    Chip,
    CircularProgress,
    Divider,
    Grid,
    IconButton,
    InputAdornment,
    MenuItem,
    Select,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material"
import RefreshIcon from "@mui/icons-material/Refresh"
import SearchIcon from "@mui/icons-material/Search"
import moment from "moment"
import { useParams } from "react-router-dom"

// ✅ IMPORTA O api (ajuste o caminho conforme a pasta onde você colocou esse componente)
import { api } from "../services/Main/Api"
import { getCondoId } from "../services/Main/AuthStorage"

const typeLabel = (t) => {
    const v = String(t || "").toLowerCase()
    if (v === "success") return "Sucesso"
    if (v === "error") return "Erro"
    if (v === "warning") return "Aviso"
    if (v === "info") return "Info"
    return t || "-"
}

const typeColor = (t) => {
    const v = String(t || "").toLowerCase()
    if (v === "success") return "success"
    if (v === "error") return "error"
    if (v === "warning") return "warning"
    if (v === "info") return "info"
    return "default"
}

function safeGetUserName(user) {
    if (!user) return "-"
    if (typeof user === "string") return user || "-"
    return user?.name || user?.first_name || user?.email || "-"
}

function normalizeResponse(resp) {
    // ✅ suporta axios (resp.data) e wrapper (resp já é o payload)
    const payload = resp?.data ?? resp
    if (Array.isArray(payload)) return payload
    if (Array.isArray(payload?.data)) return payload.data
    return []
}

export default function DocSignHistory({
                                           docId: docIdProp,
                                           title = "Atividades",
                                           showHeader = true,
                                       }) {
    const routeParams = useParams()
    const docId = docIdProp || routeParams?.docId || routeParams?.fileId || routeParams?.id

    const [rows, setRows] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState("")
    const [q, setQ] = useState("")
    const [type, setType] = useState("")

    const fetchData = useCallback(async () => {
        if (!docId) {
            setRows([])
            setError("docId não foi informado. Passe <DocSignHistory docId={...} />.")
            return
        }

        setLoading(true)
        setError("")

        try {
            const params = { condo_id: getCondoId() }

            console.log("[DocSignHistory] fetching:", { docId, params })

            const resp = await api.get(`sign/histories/${docId}/`, { params })

            console.log("[DocSignHistory] response:", resp)

            const list = normalizeResponse(resp)
            setRows(Array.isArray(list) ? list : [])
        } catch (e) {
            console.error("[DocSignHistory] error:", e)
            const msg =
                e?.response?.data?.detail ||
                e?.response?.data?.message ||
                e?.message ||
                "Não foi possível carregar o histórico."
            setError(msg)
            setRows([])
        } finally {
            setLoading(false)
        }
    }, [docId])

    useEffect(() => {
        fetchData()
    }, [fetchData])

    const typesAvailable = useMemo(() => {
        const set = new Set()
        rows.forEach((r) => r?.type && set.add(String(r.type)))
        return Array.from(set).sort()
    }, [rows])

    const filtered = useMemo(() => {
        const query = q.trim().toLowerCase()

        return (rows || [])
            .filter((r) => {
                if (!type) return true
                return String(r?.type || "").toLowerCase() === String(type).toLowerCase()
            })
            .filter((r) => {
                if (!query) return true
                const blob = [
                    r?.id,
                    r?.company,
                    r?.file,
                    r?.activity,
                    r?.time,
                    r?.type,
                    safeGetUserName(r?.user),
                    r?.user?.email,
                ]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase()
                return blob.includes(query)
            })
            .sort((a, b) => {
                const da = moment(a?.time, "DD/MM/YYYY HH:mm:ss", true)
                const db = moment(b?.time, "DD/MM/YYYY HH:mm:ss", true)
                if (da.isValid() && db.isValid()) return db.valueOf() - da.valueOf()
                return Number(b?.id || 0) - Number(a?.id || 0)
            })
    }, [rows, q, type])

    return (
        <Card>
            {showHeader && (
                <>
                    <CardHeader
                        title={
                            <Box>
                                <Typography variant="h6">{title}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Documento: <b>{docId || "-"}</b>
                                </Typography>
                            </Box>
                        }
                        action={
                            <Tooltip title="Recarregar">
                <span>
                  <IconButton onClick={fetchData} disabled={loading}>
                    <RefreshIcon />
                  </IconButton>
                </span>
                            </Tooltip>
                        }
                    />
                    <Divider />
                </>
            )}

            <CardContent>


                <Box sx={{ mt: 2 }}>
                    {loading && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={18} />
                            <Typography variant="body2" color="text.secondary">
                                Carregando histórico...
                            </Typography>
                        </Box>
                    )}

                    {!loading && error && (
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!loading && !error && (
                        <>
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    mt: 2,
                                    mb: 1,
                                }}
                            >



                            </Box>

                            <Box sx={{ overflowX: "auto" }}>
                                <Table size="small">


                                    <TableBody>
                                        {filtered.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={4}>
                                                    <Typography variant="body2" color="text.secondary">
                                                        Nenhum registro encontrado.
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filtered.map((r) => (
                                                <TableRow key={r?.id}>
                                                    <TableCell>
                                                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                                                            {r?.activity || "-"}
                                                        </Typography>


                                                            <Typography variant="caption" color="text.secondary">
                                                                {r?.time
                                                                    ? moment(r.time, "DD/MM/YYYY HH:mm:ss", true).isValid()
                                                                        ? moment(r.time, "DD/MM/YYYY HH:mm:ss").format("DD/MM/YYYY [às] HH:mm")
                                                                        : r.time
                                                                    : "-"}
                                                            </Typography>

                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </>
                    )}
                </Box>
            </CardContent>
        </Card>
    )
}
